"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Cpu, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/ca4d2b98-0711-41d7-bcc7-3111870fb241.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
const CONTENT = {
  eyebrow: {
    en: "Lab Experience Session",
    vi: "Buổi Trải Nghiệm Phòng Lab Thực Tế"
  },
  heading: {
    en: "See a class in action before you decide",
    vi: "Trải nghiệm lớp học Robotics thực tế trước khi đăng ký"
  },
  description: {
    en: "Every trial session includes an individualized skills evaluation, direct access to lab robotics kits, and dedicated guidance from our senior engineering mentors.",
    vi: "Mỗi buổi học thử bao gồm đánh giá năng lực cá nhân hóa, thực hành trực tiếp với bộ kit Robotics và nhận sự kèm cặp tận tâm từ các chuyên gia Engineering giàu kinh nghiệm."
  },
  perks: [{
    title: {
      en: "Skills evaluation",
      vi: "Đánh giá năng lực"
    },
    desc: {
      en: "Personalized placement review",
      vi: "Khảo sát tư duy & định hướng lộ trình"
    }
  }, {
    title: {
      en: "Full hardware access",
      vi: "Trải nghiệm thiết bị thật"
    },
    desc: {
      en: "Work with real robotics kits",
      vi: "Lắp ráp với bộ kit Robotics chính khóa"
    }
  }, {
    title: {
      en: "1:6 mentor ratio",
      vi: "Tỷ lệ chuẩn 1:6"
    },
    desc: {
      en: "Dedicated instructor guidance",
      vi: "Giảng viên kèm cặp và hỗ trợ sát sao"
    }
  }],
  cta: {
    en: "Book a Free Trial Session",
    vi: "Đăng Ký Học Thử Miễn Phí"
  },
  footerNote: {
    en: "Ages 4–16 · 60-Minute Session · No Commitment Required",
    vi: "Độ tuổi 4–16 · Thời lượng 60 phút · Hoàn toàn không ràng buộc"
  }
};
export default function Trial() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") {
        setLang(saved);
      }
    } catch {
      // LocalStorage access may be restricted
    }
    const handleCustomChange = (e: Event) => {
      const custom = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (custom.detail?.lang === "vi" || custom.detail?.lang === "en") {
        setLang(custom.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  return <section id="book-a-trial-cta" data-nav="light" className="relative w-full bg-background py-16 md:py-20 lg:py-24 border-t border-border overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative mx-auto max-w-4xl rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 md:p-16 shadow-2xl border border-primary/40 overflow-hidden">
          {/* Subtle blueprint grid motif */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "32px 32px"
        }} />

          {/* Top precision spec rail */}
          <div className="absolute top-0 left-8 right-8 h-[2px] bg-secondary/30 flex items-center">
            <span className="w-2 h-2 rounded-xs bg-secondary" />
          </div>

          <div className="relative z-10 flex flex-col items-center text-center pb-10 sm:pb-0">
            {/* Technical eyebrow chip with pastel highlight */}
            <div className="inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/15 px-3.5 py-1 text-xs font-mono tracking-wide text-secondary uppercase mb-6">
              <Cpu className="size-3.5" />
              <span>{CONTENT.eyebrow[lang]}</span>
            </div>

            {/* Headline */}
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-primary-foreground max-w-2xl leading-[1.15]">
              {CONTENT.heading[lang]}
            </h2>

            {/* Supporting line */}
            <p className="mt-4 max-w-xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed font-sans">
              {CONTENT.description[lang]}
            </p>

            {/* Perks grid */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-2xl text-left">
              {CONTENT.perks.map((perk, i) => <div key={i} data-index={i} className="rounded-lg bg-primary-foreground/5 border border-primary-foreground/10 p-3.5 flex items-start gap-3">
                  <div className="rounded-md bg-secondary/15 p-1 text-secondary shrink-0 mt-0.5">
                    <CheckCircle2 className="size-4 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary-foreground">
                      {perk.title[lang]}
                    </p>
                    <p className="text-xs text-primary-foreground/70 mt-0.5">
                      {perk.desc[lang]}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Call to action button */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground font-semibold px-8 py-3.5 rounded-lg shadow-md hover:bg-accent/90 transition-[color,background-color,border-color,box-shadow] duration-200">
                <Link to="/FreeTrialContactPage" className="inline-flex items-center justify-center gap-2 text-base">
                  <Sparkles className="size-4" />
                  <span>{CONTENT.cta[lang]}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Reassuring note */}
            <p className="mt-4 text-xs font-mono text-primary-foreground/60 tracking-normal max-w-[280px] sm:max-w-md mx-auto">
              {CONTENT.footerNote[lang]}
            </p>
          </div>

          {/* Static floating decorative mascot */}
          <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt="" aria-hidden="true" loading="lazy" className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 md:bottom-6 md:right-6 lg:bottom-6 lg:right-8 w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain bg-transparent pointer-events-none select-none z-0 drop-shadow-md" />
        </motion.div>
      </div>
    </section>;
}